/**
 * Gas Mix Calculation Utilities
 * Contains pure calculation functions extracted from the main component
 * for better testability and reusability.
 */

import { 
  GasType, 
  CalculationResults, 
  ValidationErrors, 
  CalculationMode 
} from '../types/gasTypes';

/**
 * Parameters for mixture calculations
 */
export interface MixtureCalculationParams {
  gasTypes: GasType[];
  budget: number;
  targetValue: number; // alcohol percentage or octane rating
}

/**
 * Result of calculation validation
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
}

/**
 * Helper function to convert string or number to number for validation
 */
const toNumber = (value: number | string): number => {
  if (typeof value === 'string') {
    return value === '' ? 0 : parseFloat(value) || 0;
  }
  return value;
};

/**
 * Helper function to check if a value is empty (empty string only)
 * Note: 0 is a valid value for alcohol percentage
 */
const isEmpty = (value: number | string): boolean => {
  return value === '';
};

/**
 * Validates calculation inputs
 */
export const validateCalculationInputs = (
  gasTypes: GasType[],
  budget: number | string,
  targetAlcoholPercent: number | string,
  targetOctane: number | string,
  calculationMode: CalculationMode
): ValidationResult => {
  const errors: ValidationErrors = {};
  
  const budgetNum = toNumber(budget);
  if (isEmpty(budget)) {
    errors.budget = 'Budget is required';
  } else if (budgetNum <= 0) {
    errors.budget = 'Budget must be greater than 0';
  }
  
  if (calculationMode === 'alcohol') {
    const targetAlcoholNum = toNumber(targetAlcoholPercent);
    if (isEmpty(targetAlcoholPercent)) {
      errors.targetAlcoholPercent = 'Target alcohol percentage is required';
    } else if (targetAlcoholNum < 0 || targetAlcoholNum > 100) {
      errors.targetAlcoholPercent = 'Target alcohol percentage must be between 0 and 100';
    }
  } else {
    const targetOctaneNum = toNumber(targetOctane);
    if (isEmpty(targetOctane)) {
      errors.targetOctane = 'Target octane is required';
    } else if (targetOctaneNum < 80 || targetOctaneNum > 120) {
      errors.targetOctane = 'Target octane must be between 80 and 120';
    }
  }
  
  if (gasTypes.length < 2) {
    errors.gasTypes = 'You need at least 2 gas types to mix';
  }
  
  // Validate gas types have positive values
  gasTypes.forEach((gas, index) => {
    const priceNum = toNumber(gas.pricePerLiter);
    const alcoholNum = toNumber(gas.alcoholPercent);
    const octaneNum = toNumber(gas.octaneRating);
    
    if (isEmpty(gas.pricePerLiter)) {
      errors[`gasType${gas.id}Price`] = `Gas type ${index + 1} price is required`;
    } else if (priceNum <= 0) {
      errors[`gasType${gas.id}Price`] = `Gas type ${index + 1} price must be greater than 0`;
    }
    
    if (isEmpty(gas.alcoholPercent)) {
      errors[`gasType${gas.id}Alcohol`] = `Gas type ${index + 1} alcohol percentage is required`;
    } else if (alcoholNum < 0 || alcoholNum > 100) {
      errors[`gasType${gas.id}Alcohol`] = `Gas type ${index + 1} alcohol percentage must be between 0 and 100`;
    }
    
    if (isEmpty(gas.octaneRating)) {
      errors[`gasType${gas.id}Octane`] = `Gas type ${index + 1} octane rating is required`;
    } else if (octaneNum < 80 || octaneNum > 120) {
      errors[`gasType${gas.id}Octane`] = `Gas type ${index + 1} octane rating must be between 80 and 120`;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Calculates optimal mixture for alcohol percentage target
 */
export const calculateAlcoholMix = (params: MixtureCalculationParams): CalculationResults | null => {
  const { gasTypes, budget, targetValue: targetAlcoholPercent } = params;
  const sortedGas = [...gasTypes].sort((a, b) => toNumber(a.alcoholPercent) - toNumber(b.alcoholPercent));
  
  // Check if target is achievable
  const minAlcohol = toNumber(sortedGas[0].alcoholPercent);
  const maxAlcohol = toNumber(sortedGas[sortedGas.length - 1].alcoholPercent);
  
  if (targetAlcoholPercent < minAlcohol || targetAlcoholPercent > maxAlcohol) {
    throw new Error(`Target alcohol percentage must be between ${minAlcohol}% and ${maxAlcohol}%`);
  }

  // For 2 gas types, solve directly
  if (gasTypes.length === 2) {
    return calculateTwoGasAlcoholMix(sortedGas, budget, targetAlcoholPercent, gasTypes);
  } else {
    // For multiple gas types, find best pair combination
    return calculateMultiGasAlcoholMix(sortedGas, budget, targetAlcoholPercent, gasTypes);
  }
};

/**
 * Calculates alcohol mix for exactly 2 gas types
 */
const calculateTwoGasAlcoholMix = (
  sortedGas: GasType[], 
  budget: number, 
  targetAlcoholPercent: number,
  originalGasTypes: GasType[]
): CalculationResults | null => {
  const [gas1, gas2] = sortedGas;
  
  const A1 = toNumber(gas1.alcoholPercent) / 100;
  const A2 = toNumber(gas2.alcoholPercent) / 100;
  const At = targetAlcoholPercent / 100;
  const P1 = toNumber(gas1.pricePerLiter);
  const P2 = toNumber(gas2.pricePerLiter);
  
  // Handle edge case where target equals one of the gas percentages
  if (At === A1) {
    // Use only gas1
    const V1 = budget / P1;
    return {
      mixtures: originalGasTypes.map(g => ({
        ...g,
        alcoholPercent: toNumber(g.alcoholPercent),
        octaneRating: toNumber(g.octaneRating),
        pricePerLiter: toNumber(g.pricePerLiter),
        volume: g.id === gas1.id ? V1 : 0,
        cost: g.id === gas1.id ? V1 * P1 : 0
      })),
      totalVolume: V1,
      totalCost: budget,
      actualAlcoholPercent: toNumber(gas1.alcoholPercent),
      actualOctane: toNumber(gas1.octaneRating),
      remainingBudget: 0
    };
  }
  
  if (At === A2) {
    // Use only gas2
    const V2 = budget / P2;
    return {
      mixtures: originalGasTypes.map(g => ({
        ...g,
        alcoholPercent: toNumber(g.alcoholPercent),
        octaneRating: toNumber(g.octaneRating),
        pricePerLiter: toNumber(g.pricePerLiter),
        volume: g.id === gas2.id ? V2 : 0,
        cost: g.id === gas2.id ? V2 * P2 : 0
      })),
      totalVolume: V2,
      totalCost: budget,
      actualAlcoholPercent: toNumber(gas2.alcoholPercent),
      actualOctane: toNumber(gas2.octaneRating),
      remainingBudget: 0
    };
  }

  // Handle edge case where A1 equals At (both could be 0)
  if (Math.abs(A1 - At) < 1e-10) {
    throw new Error('Division by zero: gas type and target have same alcohol percentage');
  }
  
  const ratio = (A2 - At) / (At - A1);
  if (ratio < 0) {
    throw new Error('Invalid mixture ratio calculated');
  }
  
  const V2 = budget / (ratio * P1 + P2);
  const V1 = V2 * ratio;
  
  if (V1 < 0 || V2 < 0) {
    throw new Error('No valid solution found within budget');
  }
  
  const totalVolume = V1 + V2;
  const actualAlcoholPercent = (V1 * A1 + V2 * A2) / totalVolume * 100;
  const actualOctane = (V1 * toNumber(gas1.octaneRating) + V2 * toNumber(gas2.octaneRating)) / totalVolume;
  
  return {
    mixtures: originalGasTypes.map(g => ({
      id: g.id,
      name: g.name,
      alcoholPercent: toNumber(g.alcoholPercent),
      octaneRating: toNumber(g.octaneRating),
      pricePerLiter: toNumber(g.pricePerLiter),
      volume: g.id === gas1.id ? V1 : g.id === gas2.id ? V2 : 0,
      cost: g.id === gas1.id ? V1 * P1 : g.id === gas2.id ? V2 * P2 : 0
    })),
    totalVolume,
    totalCost: budget,
    actualAlcoholPercent,
    actualOctane,
    remainingBudget: 0
  };
};

/**
 * Calculates alcohol mix for multiple gas types by finding best pair
 */
const calculateMultiGasAlcoholMix = (
  sortedGas: GasType[], 
  budget: number, 
  targetAlcoholPercent: number,
  originalGasTypes: GasType[]
): CalculationResults | null => {
  let bestSolution: CalculationResults | null = null;
  let maxVolume = 0;
  
  for (let i = 0; i < sortedGas.length - 1; i++) {
    for (let j = i + 1; j < sortedGas.length; j++) {
      const gas1 = sortedGas[i];
      const gas2 = sortedGas[j];
      
      if (targetAlcoholPercent >= toNumber(gas1.alcoholPercent) && 
          targetAlcoholPercent <= toNumber(gas2.alcoholPercent)) {
        
        const A1 = toNumber(gas1.alcoholPercent) / 100;
        const A2 = toNumber(gas2.alcoholPercent) / 100;
        const At = targetAlcoholPercent / 100;
        const P1 = toNumber(gas1.pricePerLiter);
        const P2 = toNumber(gas2.pricePerLiter);
        
        // Handle edge case where A1 equals At (both could be 0)
        if (Math.abs(A1 - At) < 1e-10) {
          continue; // Skip this combination as it would cause division by zero
        }
        
        const ratio = (A2 - At) / (At - A1);
        const V2 = budget / (ratio * P1 + P2);
        const V1 = V2 * ratio;
        
        if (V1 >= 0 && V2 >= 0) {
          const totalVolume = V1 + V2;
          
          if (totalVolume > maxVolume) {
            maxVolume = totalVolume;
            const actualOctane = (V1 * toNumber(gas1.octaneRating) + V2 * toNumber(gas2.octaneRating)) / totalVolume;
            
            bestSolution = {
              mixtures: originalGasTypes.map(g => ({
                ...g,
                alcoholPercent: toNumber(g.alcoholPercent),
                octaneRating: toNumber(g.octaneRating),
                pricePerLiter: toNumber(g.pricePerLiter),
                volume: g.id === gas1.id ? V1 : g.id === gas2.id ? V2 : 0,
                cost: g.id === gas1.id ? V1 * P1 : g.id === gas2.id ? V2 * P2 : 0
              })),
              totalVolume,
              totalCost: budget,
              actualAlcoholPercent: targetAlcoholPercent,
              actualOctane,
              remainingBudget: 0
            };
          }
        }
      }
    }
  }
  
  if (!bestSolution) {
    throw new Error('No valid solution found within budget constraints');
  }
  
  return bestSolution;
};

/**
 * Calculates optimal mixture for octane rating target
 */
export const calculateOctaneMix = (params: MixtureCalculationParams): CalculationResults | null => {
  const { gasTypes, budget, targetValue: targetOctane } = params;
  const sortedGas = [...gasTypes].sort((a, b) => toNumber(a.octaneRating) - toNumber(b.octaneRating));
  
  // Check if target is achievable
  const minOctane = toNumber(sortedGas[0].octaneRating);
  const maxOctane = toNumber(sortedGas[sortedGas.length - 1].octaneRating);
  
  if (targetOctane < minOctane || targetOctane > maxOctane) {
    throw new Error(`Target octane rating must be between ${minOctane} and ${maxOctane}`);
  }

  // For 2 gas types, solve directly using octane mixing
  if (gasTypes.length === 2) {
    return calculateTwoGasOctaneMix(sortedGas, budget, targetOctane, gasTypes);
  } else {
    // For multiple gas types, find best pair combination
    return calculateMultiGasOctaneMix(sortedGas, budget, targetOctane, gasTypes);
  }
};

/**
 * Calculates octane mix for exactly 2 gas types
 */
const calculateTwoGasOctaneMix = (
  sortedGas: GasType[], 
  budget: number, 
  targetOctane: number,
  originalGasTypes: GasType[]
): CalculationResults | null => {
  const [gas1, gas2] = sortedGas;
  
  const O1 = toNumber(gas1.octaneRating);
  const O2 = toNumber(gas2.octaneRating);
  const Ot = targetOctane;
  const P1 = toNumber(gas1.pricePerLiter);
  const P2 = toNumber(gas2.pricePerLiter);
  
  // Octane mixing equation: V1 * O1 + V2 * O2 = (V1 + V2) * Ot
  // Budget equation: V1 * P1 + V2 * P2 = Budget
  
  // Handle edge case where target equals one of the gas octane ratings
  if (Ot === O1) {
    // Use only gas1
    const V1 = budget / P1;
    return {
      mixtures: originalGasTypes.map(g => ({
        ...g,
        alcoholPercent: toNumber(g.alcoholPercent),
        octaneRating: toNumber(g.octaneRating),
        pricePerLiter: toNumber(g.pricePerLiter),
        volume: g.id === gas1.id ? V1 : 0,
        cost: g.id === gas1.id ? V1 * P1 : 0
      })),
      totalVolume: V1,
      totalCost: budget,
      actualAlcoholPercent: toNumber(gas1.alcoholPercent),
      actualOctane: toNumber(gas1.octaneRating),
      remainingBudget: 0
    };
  }
  
  if (Ot === O2) {
    // Use only gas2
    const V2 = budget / P2;
    return {
      mixtures: originalGasTypes.map(g => ({
        ...g,
        alcoholPercent: toNumber(g.alcoholPercent),
        octaneRating: toNumber(g.octaneRating),
        pricePerLiter: toNumber(g.pricePerLiter),
        volume: g.id === gas2.id ? V2 : 0,
        cost: g.id === gas2.id ? V2 * P2 : 0
      })),
      totalVolume: V2,
      totalCost: budget,
      actualAlcoholPercent: toNumber(gas2.alcoholPercent),
      actualOctane: toNumber(gas2.octaneRating),
      remainingBudget: 0
    };
  }

  // Handle edge case where O1 equals Ot
  if (Math.abs(O1 - Ot) < 1e-10) {
    throw new Error('Division by zero: gas type and target have same octane rating');
  }
  
  const ratio = (O2 - Ot) / (Ot - O1);
  if (ratio < 0) {
    throw new Error('Invalid mixture ratio calculated for octane');
  }
  
  const V2 = budget / (ratio * P1 + P2);
  const V1 = V2 * ratio;
  
  if (V1 < 0 || V2 < 0) {
    throw new Error('No valid octane solution found within budget');
  }
  
  const totalVolume = V1 + V2;
  const actualOctane = (V1 * O1 + V2 * O2) / totalVolume;
  const actualAlcoholPercent = (V1 * toNumber(gas1.alcoholPercent) + V2 * toNumber(gas2.alcoholPercent)) / totalVolume;
  
  return {
    mixtures: originalGasTypes.map(g => ({
      id: g.id,
      name: g.name,
      alcoholPercent: toNumber(g.alcoholPercent),
      octaneRating: toNumber(g.octaneRating),
      pricePerLiter: toNumber(g.pricePerLiter),
      volume: g.id === gas1.id ? V1 : g.id === gas2.id ? V2 : 0,
      cost: g.id === gas1.id ? V1 * P1 : g.id === gas2.id ? V2 * P2 : 0
    })),
    totalVolume,
    totalCost: budget,
    actualAlcoholPercent,
    actualOctane,
    remainingBudget: 0
  };
};

/**
 * Calculates octane mix for multiple gas types by finding best pair
 */
const calculateMultiGasOctaneMix = (
  sortedGas: GasType[], 
  budget: number, 
  targetOctane: number,
  originalGasTypes: GasType[]
): CalculationResults | null => {
  let bestSolution: CalculationResults | null = null;
  let maxVolume = 0;
  
  for (let i = 0; i < sortedGas.length - 1; i++) {
    for (let j = i + 1; j < sortedGas.length; j++) {
      const gas1 = sortedGas[i];
      const gas2 = sortedGas[j];
      
      if (targetOctane >= toNumber(gas1.octaneRating) && 
          targetOctane <= toNumber(gas2.octaneRating)) {
        
        const O1 = toNumber(gas1.octaneRating);
        const O2 = toNumber(gas2.octaneRating);
        const Ot = targetOctane;
        const P1 = toNumber(gas1.pricePerLiter);
        const P2 = toNumber(gas2.pricePerLiter);
        
        // Handle edge case where O1 equals Ot
        if (Math.abs(O1 - Ot) < 1e-10) {
          continue; // Skip this combination as it would cause division by zero
        }
        
        const ratio = (O2 - Ot) / (Ot - O1);
        const V2 = budget / (ratio * P1 + P2);
        const V1 = V2 * ratio;
        
        if (V1 >= 0 && V2 >= 0) {
          const totalVolume = V1 + V2;
          
          if (totalVolume > maxVolume) {
            maxVolume = totalVolume;
            const actualAlcoholPercent = (V1 * toNumber(gas1.alcoholPercent) + V2 * toNumber(gas2.alcoholPercent)) / totalVolume;
            
            bestSolution = {
              mixtures: originalGasTypes.map(g => ({
                id: g.id,
                name: g.name,
                alcoholPercent: toNumber(g.alcoholPercent),
                octaneRating: toNumber(g.octaneRating),
                pricePerLiter: toNumber(g.pricePerLiter),
                volume: g.id === gas1.id ? V1 : g.id === gas2.id ? V2 : 0,
                cost: g.id === gas1.id ? V1 * P1 : g.id === gas2.id ? V2 * P2 : 0
              })),
              totalVolume,
              totalCost: budget,
              actualAlcoholPercent,
              actualOctane: targetOctane,
              remainingBudget: 0
            };
          }
        }
      }
    }
  }
  
  if (!bestSolution) {
    throw new Error('No valid octane solution found within budget constraints');
  }
  
  return bestSolution;
};

/**
 * Main calculation function that handles both modes
 */
export const calculateOptimalMix = (
  gasTypes: GasType[],
  budget: number | string,
  targetAlcoholPercent: number | string,
  targetOctane: number | string,
  calculationMode: CalculationMode
): CalculationResults => {
  // Validate inputs
  const validation = validateCalculationInputs(
    gasTypes, 
    budget, 
    targetAlcoholPercent, 
    targetOctane, 
    calculationMode
  );
  
  if (!validation.isValid) {
    const firstError = Object.values(validation.errors)[0];
    throw new Error(firstError || 'Validation failed');
  }

  const params: MixtureCalculationParams = {
    gasTypes,
    budget: toNumber(budget),
    targetValue: toNumber(calculationMode === 'alcohol' ? targetAlcoholPercent : targetOctane)
  };

  let result: CalculationResults | null = null;

  if (calculationMode === 'alcohol') {
    result = calculateAlcoholMix(params);
  } else {
    result = calculateOctaneMix(params);
  }

  if (!result) {
    throw new Error('No valid solution found');
  }

  return result;
};

/**
 * Utility function to check if target is achievable with given gas types
 */
export const isTargetAchievable = (
  gasTypes: GasType[],
  target: number,
  mode: CalculationMode
): { achievable: boolean; minValue: number; maxValue: number } => {
  if (gasTypes.length === 0) {
    return {
      achievable: false,
      minValue: Infinity,
      maxValue: -Infinity
    };
  }

  if (mode === 'alcohol') {
    const alcoholValues = gasTypes.map(g => toNumber(g.alcoholPercent)).sort((a, b) => a - b);
    return {
      achievable: target >= alcoholValues[0] && target <= alcoholValues[alcoholValues.length - 1],
      minValue: alcoholValues[0],
      maxValue: alcoholValues[alcoholValues.length - 1]
    };
  } else {
    const octaneValues = gasTypes.map(g => toNumber(g.octaneRating)).sort((a, b) => a - b);
    return {
      achievable: target >= octaneValues[0] && target <= octaneValues[octaneValues.length - 1],
      minValue: octaneValues[0],
      maxValue: octaneValues[octaneValues.length - 1]
    };
  }
};